<!--
  WORKSTREAM N — the routed risk-item page. The toolbar is `patient-task-detail.component.html`
  verbatim in shape: back, title, a DELETE THAT ONLY EXISTS ONCE THERE IS AN ID, and save.
-->
<mat-toolbar color="primary" class="action-toolbar">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back">
        <mat-icon color="primary">arrow_back_ios</mat-icon>
    </button>
    <h3>{{ patient | patientHeading:'Risk Item' }}</h3>
    <div>
        <!-- N1 — gated on the row having an id, exactly as `patient-task-detail.component.html:7`.
             The add route has no row yet, so there is nothing to delete and no button. -->
        <button mat-icon-button *ngIf="editingRiskItem && editingRiskItem.id" (click)="deleteRiskItem()"
            matTooltip="Delete Risk Item">
            <mat-icon color="warn">delete</mat-icon>
        </button>
        <button mat-icon-button [disabled]="!canSave" (click)="saveRiskItem()" matTooltip="Save Risk Item">
            <mat-icon [color]="canSave ? 'primary' : ''">save</mat-icon>
        </button>
    </div>
</mat-toolbar>

<div class="details-container">
    <form [formGroup]="form">
        <mat-card>

            <!--
              THE VERBATIM QUOTE (D8), READ-ONLY AND FIRST.

              §1: "No editing of an item's AI-extracted quote. The quote is evidence; it is
              verbatim or it is gone." There is no input bound to it anywhere on this page. The
              doctor is putting this on their own letterhead, so they read the sentence they
              actually said before anything else.
            -->
            <div class="risk-detail-quote" *ngIf="quote">
                <div class="risk-detail-quote-label">
                    <mat-icon>format_quote</mat-icon>
                    <span>What you said (recorded verbatim, and not editable)</span>
                </div>
                <blockquote>{{ quote }}</blockquote>
            </div>

            <!-- Two different facts, deliberately worded and styled differently — the add
                 dialog's precedent. A hand-typed item never had a quote and never should;
                 warning about it would train the user to ignore the warning below, which
                 reports a real loss of the D8 evidence control. -->
            <div class="risk-detail-note" *ngIf="!quote && isManualEntry">
                <mat-icon>edit_note</mat-icon>
                <span>
                    Entered by hand, so there is no recorded quotation. The issue text below is what
                    will appear on the register and on any report.
                </span>
            </div>

            <div class="risk-detail-note risk-detail-warn" *ngIf="!quote && !isManualEntry">
                <mat-icon>report_problem</mat-icon>
                <span>
                    No verbatim quote was captured for this issue. It is on the register with no
                    supporting quotation.
                </span>
            </div>

            <mat-form-field appearance="outline">
                <mat-label>Date of issue</mat-label>
                <input matInput [matDatepicker]="riskDatePicker" formControlName="dateOfIssue"
                    placeholder="DD/MM/YYYY" [max]="todayDate" (dateChange)="onDateOfIssueChange()">
                <mat-datepicker-toggle matSuffix [for]="riskDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #riskDatePicker></mat-datepicker>
                <mat-hint>The day the issue happened at the facility, not today.</mat-hint>
            </mat-form-field>

            <!--
              THE FACILITY PICKER (N7 / §12 — THE CLIENT DECIDES).

              §4 resolves where an issue happened from admission and appointment data; it does not
              always get it right, and the doctor was standing there. So the resolved answer is
              offered as a DEFAULT and the doctor's change is what gets stored, with no server-side
              validation and nothing to reconcile afterwards.

              LIVE ON BOTH ROUTES since 2026-09-01 ("facility should be editable, we can leave it
              up to the user"), and there is no `reportedAt` gate. Leaving it alone sends no
              `facilityChoice` at all, which means two different right things: on ADD the server's
              §4 chain fills it in — still the better answer in the one case the browser cannot
              cover, a hospital deactivated since the admission was recorded — and on EDIT the
              stored snapshot is left exactly as it is, never re-resolved.

              DIRECTLY UNDER DATE OF ISSUE, and above Category, because the two together are the
              WHEN and the WHERE of the same event and the date is what §4 previews the facility
              from (`onDateOfIssueChange`). `.risk-detail-facility` carries a top margin for the
              `mat-hint` above it — a hint renders inside the field's own subscript wrapper, so
              without it this block starts hard against that sentence.

              This is the SELECTOR, not the read-only Facility block from `risk-item-detail-dialog`
              — that block stays hidden behind `showExtendedDetail`, at the user's request, and is
              not resurrected here.
            -->
            <div class="risk-detail-facility">

                <!-- No heading, no restated summary: the select below already says where this is
                     filed, and a line above it saying the same thing in prose was chrome. The
                     CONDITIONAL lines further down are NOT chrome and stay — each says something
                     the control cannot (a stored facility that is not selectable, an override the
                     doctor has made, an empty hospital list). -->
                <!--
                  ONE SELECT — the mode and the hospital are the same question.

                  In-rooms / OPD is the FIRST OPTION rather than the off position of a toggle: D2/D6
                  make it a first-class outcome, named as a place, and a list of places is where a
                  place belongs. Two controls could disagree (facility mode, no hospital); one
                  cannot, so `needsHospitalSelection`, its banner and its `canSave` term are gone.
                -->
                <mat-form-field appearance="outline">
                    <mat-label>Where this happened</mat-label>
                    <mat-select formControlName="facilitySelection" (selectionChange)="onFacilityChange()">
                        <mat-option [value]="roomsValue">In-rooms / OPD</mat-option>
                        <mat-option *ngFor="let hospital of hospitals" [value]="hospital.id">
                            {{ hospital.name }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <!-- Only once a hospital is chosen, and only when that hospital has wards. A
                     procedure is booked to a hospital and not to a bed, so no ward is a normal
                     answer and "No ward" is an explicit option rather than an empty select. -->
                <mat-form-field appearance="outline" *ngIf="selectedHospitalId && wards.length">
                    <mat-label>Ward (optional)</mat-label>
                    <mat-select formControlName="facilityWardId" (selectionChange)="onWardChange()">
                        <mat-option [value]="null">No ward / not specified</mat-option>
                        <mat-option *ngFor="let ward of wards" [value]="ward.id">{{ ward.name }}</mat-option>
                    </mat-select>
                </mat-form-field>

                <!-- Still gated on `facilityMode === 'facility'`, which now means "the sentinel is
                     not the selection" — i.e. the doctor is looking at a facility answer and the
                     list behind it is empty. Saying it when they have deliberately chosen In-rooms
                     / OPD would be noise. -->
                <p class="risk-detail-facility-hint" *ngIf="!isEditing && !hospitals.length && facilityMode === 'facility'">
                    No hospitals are loaded for this practice, so In-rooms / OPD is the only thing
                    this list can offer. Choose it, or leave this as it is and the server will
                    resolve the facility.
                </p>

                <p class="risk-detail-facility-hint" *ngIf="isEditing && !hospitals.length && facilityMode === 'facility'">
                    No hospitals are loaded for this practice, so In-rooms / OPD is the only thing
                    this list can offer. Choose it, or leave this as it is and the recorded facility
                    stays as it is.
                </p>

                <!-- STILL NEEDED WITH ONE SELECT, and for the same reason. The stored hospital may
                     not be selectable: §4 branch 3 matches an admission-history NAME against
                     `Hospitals` and misses legitimately (`Olivedale` vs `Netcare Olivedale`), and a
                     hospital deactivated since is not in `hospitals$` at all. Both leave the select
                     EMPTY beside a row that does name a facility, so without this line a blank
                     select reads as "no hospital" — and the doctor's fix for a wrong-looking blank
                     would be to pick something, which would overwrite a facility that was correct.
                     Leaving it alone sends no `facilityChoice` and the stored value survives.

                     `!facilityTouched` replaces the old `facilityMode === 'facility'` wrapper. It
                     is the more exact guard: the sentence promises "leave this alone and it stays
                     as recorded", which stops being true the moment the select is moved — including
                     the move to In-rooms / OPD, which leaves `selectedHospitalId` null and would
                     otherwise keep this line on screen contradicting the choice just made. -->
                <p class="risk-detail-facility-hint"
                    *ngIf="isEditing && !facilityTouched && !selectedHospitalId && storedFacilityLine">
                    Recorded as <strong>{{ storedFacilityLine }}</strong>, which is not among the
                    practice's current active hospitals. Leave this alone and it stays as
                    recorded; choose a hospital to replace it.
                </p>

                <!-- "SAY SO PLAINLY." The doctor has moved this away from the default — on ADD
                     what the §4 chain worked out, on EDIT what the row already says. Either way
                     the record is about to change, and this is the only place that says so before
                     the save. `originalFacilityLine` is the one that picks the right of the two. -->
                <div class="risk-detail-note risk-detail-override" *ngIf="facilityTouched">
                    <mat-icon>edit_location_alt</mat-icon>
                    <span>
                        You have changed this from <strong>{{ originalFacilityLine || 'your rooms' }}</strong>.
                        It will be filed as <strong>{{ chosenFacilityLine }}</strong> and recorded as
                        your own choice, not as a location the system worked out.
                    </span>
                </div>
            </div>

            <!-- D7 — a CLOSED taxonomy, BY IMPORT. No free text: "17 consumable stock-outs at
                 Hospital X this month" is a conversation with a hospital; 60 loose sentences is a
                 shrug. -->
            <mat-form-field appearance="outline">
                <mat-label>Category</mat-label>
                <mat-select formControlName="category">
                    <mat-option *ngFor="let category of categoryOptions" [value]="category">
                        {{ categoryLabel(category) }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Severity</mat-label>
                <mat-select formControlName="severity">
                    <mat-option *ngFor="let severity of severityOptions" [value]="severity">
                        {{ severityLabel(severity) }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!-- D9. Stored in display form and rendered raw — there is deliberately no
                 `riskStatusLabel` helper for the two forms to drift between. -->
            <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                    <mat-option *ngFor="let status of statusOptions" [value]="status">
                        {{ status }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Issue</mat-label>
                <textarea matInput cdkTextareaAutosize="true" rows="3" formControlName="issue"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Notes (optional)</mat-label>
                <textarea matInput cdkTextareaAutosize="true" rows="6" formControlName="notes"></textarea>
            </mat-form-field>

            <!-- §28 / P5-P6 — PHOTOGRAPHS. "a broken instrument for example… what if they took a
                 photo of the instrument in theatre, then are later doing their notes, this risk
                 item gets flagged, they pic the image from gallery and attach it."

                 The same `app-file-upload` the note detail uses, with the same three outputs, so
                 camera / paste / gallery / editing all come for free. `allowedFileTypes`
                 admits PDFs as well as images: the server rasterises a PDF into page images
                 and the gallery shows those, hiding the PDF row itself. A scanned incident
                 form is evidence a hospital acts on. -->
            <div class="risk-media">
                <app-file-upload [fileObjects]="loadedMediaObjects" [allowMultiple]="true"
                    [allowedFileTypes]="'image/*, application/pdf'" title="Photographs"
                    hintMessage="Attach a photo of the equipment, consumable or facility issue."
                    (fileUpload)="onRiskMediaUpload($event)" (fileDelete)="onRiskMediaDelete($event)"
                    (filesEdited)="onRiskMediaEdited($event)" [cameraEnabled]="true"
                    [enableEditing]="true">
                </app-file-upload>
                <!-- The `Reported` divergence. Attaching after reporting is ALLOWED (user
                     decision, 2026-09-01: "leave it up to the user, same as the facility edit") —
                     this is not a warning and gates nothing, it is the honest fact that the
                     document the hospital holds predates these photographs. -->
                <div class="risk-media-hint" *ngIf="photosAddedAfterReporting > 0">
                    This issue was reported on {{ editingRiskItem?.reportedAt | date : 'dd/MM/yyyy' }}.
                    {{ photosAddedAfterReporting }}
                    {{ photosAddedAfterReporting === 1 ? 'photograph was' : 'photographs were' }}
                    attached afterwards and {{ photosAddedAfterReporting === 1 ? 'is' : 'are' }} not
                    in the document that was sent.
                </div>
            </div>

        </mat-card>
    </form>
</div>
